/**
 * ACTIVITY-CORE (L2) — CATALOG trash.
 *
 * Only catalog/master-data entities have a trash: menu, variants,
 * combos, ingredients, suppliers, employees, customers, and promotions.
 * The transactional domain (paid orders, payments, cash register, stock) does
 * NOT go through here: its corrections are explicit business operations that
 * remain in history (POS variant §1b of TRACEABILITY_PLAN).
 */
import {
  and,
  combo,
  customer,
  desc,
  employee,
  eq,
  ingredient,
  isNotNull,
  menu_category,
  menu_product,
  type PowerSyncDrizzleDB,
  product_variant,
  promotion,
  supplier,
} from "@comandero/powersync";

/** Entity types with a trash (catalog). */
export type TrashEntityType =
  | "menu_category"
  | "menu_product"
  | "product_variant"
  | "combo"
  | "ingredient"
  | "supplier"
  | "employee"
  | "customer"
  | "promotion";

export const TRASH_ENTITY_TYPES: readonly TrashEntityType[] = [
  "menu_category",
  "menu_product",
  "product_variant",
  "combo",
  "ingredient",
  "supplier",
  "employee",
  "customer",
  "promotion",
] as const;

/** Normalized trash item (union of the catalog tables). */
export interface TrashItem {
  entityType: TrashEntityType;
  id: string;
  name: string;
  deletedAt: string;
}

const TRASH_TABLES = {
  menu_category,
  menu_product,
  product_variant,
  combo,
  ingredient,
  supplier,
  employee,
  customer,
  promotion,
} as const;

/**
 * "Watch" SQL for PowerSync: references ALL trash tables so `resolveTables`
 * subscribes the view to their changes (the real query is the client-side union
 * in `getTrashItemsQuery`).
 */
export function getTrashWatchSql(): { sql: string; params: unknown[] } {
  const sql = TRASH_ENTITY_TYPES.map((t) => `SELECT id FROM ${t}`).join(" UNION ALL ");
  return { sql, params: [] };
}

/**
 * All catalog items in the organization's trash, newest first. Joins the tables
 * client-side (drizzle SQLite has no convenient typed UNION, and these are
 * inexpensive local queries).
 */
export async function getTrashItemsQuery(
  db: PowerSyncDrizzleDB,
  organizationId: string,
): Promise<TrashItem[]> {
  const items: TrashItem[] = [];
  for (const entityType of TRASH_ENTITY_TYPES) {
    const table = TRASH_TABLES[entityType];
    const rows = await db
      .select({ id: table.id, name: table.name, deleted_at: table.deleted_at })
      .from(table)
      .where(and(eq(table.organization_id, organizationId), isNotNull(table.deleted_at)))
      .orderBy(desc(table.deleted_at));
    for (const row of rows) {
      items.push({
        entityType,
        id: row.id,
        name: row.name ?? "",
        deletedAt: row.deleted_at ?? "",
      });
    }
  }
  return items.toSorted((a, b) => (a.deletedAt < b.deletedAt ? 1 : -1));
}
